import type { BackgroundWorkItem } from '@localagent/sdk';
import { cn } from '@localagent/ui';
import { useOpenRunningWork } from '../running-work-focus';
import { formatElapsed, ms, type VisibleRunningWork } from './running-work';

const KIND_LABEL: Record<BackgroundWorkItem['kind'], string> = { agent: 'Agent', tool: 'Tool', plan: 'Plan' };
const OUTCOME_LABEL: Record<Exclude<BackgroundWorkItem['status'], 'running'>, string> = {
  completed: 'done',
  failed: 'failed',
  stopped: 'stopped',
};

function WorkRow({ item, now, testIdPrefix }: { item: BackgroundWorkItem; now: number; testIdPrefix: string }) {
  const open = useOpenRunningWork();
  const clickable = open !== null && item.tabParticipantId !== null;
  const running = item.status === 'running';
  const started = ms(item.startedAt);
  const ended = ms(item.endedAt);
  const timing = running
    ? started === null ? '' : formatElapsed(started, now)
    : ended === null ? '' : `${formatElapsed(ended, now)} ago`;
  const body = (
    <>
      <span aria-hidden="true" className="grid h-4 w-4 place-items-center">
        {running ? (
          <span data-testid={`${testIdPrefix}running-work-spinner`} className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-info-text border-r-transparent motion-reduce:animate-none" />
        ) : (
          <span className={item.status === 'completed' ? 'text-success-text' : 'text-muted-foreground'}>{item.status === 'completed' ? '✓' : '•'}</span>
        )}
      </span>
      <span className="min-w-0">
        <span className={cn('block truncate', running ? 'text-foreground' : 'text-muted-foreground')}>
          {item.label}
          {!running && (
            <span data-testid={`${testIdPrefix}running-work-outcome`} className="text-caption text-muted-foreground"> · {KIND_LABEL[item.kind]} · {OUTCOME_LABEL[item.status as keyof typeof OUTCOME_LABEL]}</span>
          )}
        </span>
        {running && (
          <span data-testid={`${testIdPrefix}running-work-detail`} className="block truncate text-caption text-muted-foreground">
            {KIND_LABEL[item.kind]}{item.detail ? ` · ${item.detail}` : ''}
          </span>
        )}
      </span>
      <span data-testid={`${testIdPrefix}running-work-time`} className="text-caption text-muted-foreground">{timing}</span>
      <span aria-hidden="true" className="text-muted-foreground">{clickable ? '›' : ''}</span>
    </>
  );
  const shared = {
    'data-testid': `${testIdPrefix}running-work-row`,
    'data-work-id': item.id,
    'data-kind': item.kind,
    'data-status': item.status,
    className: 'grid w-full grid-cols-[1rem_minmax(0,1fr)_auto_0.75rem] items-center gap-2 px-3 py-1.5 text-left text-micro',
  };
  if (!clickable) return <div {...shared}>{body}</div>;
  return (
    <button type="button" {...shared} onClick={() => open(item)} aria-label={`Open ${item.label}`} className={cn(shared.className, 'hover:bg-accent')}>
      {body}
    </button>
  );
}

export interface RunningWorkSectionProps {
  work: VisibleRunningWork;
  testIdPrefix?: string;
}

export function RunningWorkSection({ work, testIdPrefix = '' }: RunningWorkSectionProps) {
  if (work.running.length === 0 && work.recent.length === 0) return null;
  return (
    <div aria-label="Running work" data-testid={`${testIdPrefix}running-work`} data-running-count={work.running.length} className="border-b border-border py-1">
      {work.running.length > 0 && (
        <>
          <div className="flex justify-between px-3 pb-1 pt-0.5 text-micro uppercase tracking-wide text-muted-foreground">
            <span>Running now</span>
            <span>{work.running.length}</span>
          </div>
          {work.running.map((item) => (
            <WorkRow key={item.id} item={item} now={work.now} testIdPrefix={testIdPrefix} />
          ))}
        </>
      )}
      {work.recent.length > 0 && (
        <>
          <div data-testid={`${testIdPrefix}running-work-recent`} className="px-3 pb-1 pt-1.5 text-micro uppercase tracking-wide text-muted-foreground">Recently finished</div>
          {work.recent.map((item) => (
            <WorkRow key={item.id} item={item} now={work.now} testIdPrefix={testIdPrefix} />
          ))}
        </>
      )}
    </div>
  );
}
